/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { Typography } from "@insite/mobius/index";
import { SelectPresentationProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect } from "react-redux";

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
}

export interface ProductDetailsPriceStyles {
    productPrice?: ProductPriceStyles;
    // custom
    xProductDetailsPriceStyles?: XStyles;
}

export const priceStyles: ProductDetailsPriceStyles = {
    productPrice: {
        price: {
            priceText: { size: 26 },
            unitOfMeasureText: { size: 26 },
        },
    },
};

const styles = priceStyles;

const ProductDetailsPrice: React.FC<OwnProps> = ({ productContext, productSettings }) => {
    // XNG-Change
    const { product, productInfo } = productContext;

    const msrpPrices = React.useMemo(() => {
        try {
            return JSON.parse(product.properties.msrpPrices || "{}");
        } catch {
            return {};
        }
    }, [product.properties.msrpPrices]);

    // xng-change: Update the template to handle the case where no variant selected
    return (
        <>
            <Typography {...(styles as any).productListPrice}>
                {product.isVariantParent && "$--"}
                {/* XNG-Change */}
                {!product.isVariantParent && msrpPrices?.[productInfo.unitOfMeasure]}
                <Typography {...(styles as any).msrp}>{translate("MSRP")}</Typography>
            </Typography>
            {product.isVariantParent && (
                <Typography {...(styles as any).productPriceReplcement}>
                    $-- <Typography>/ UNIT</Typography>
                </Typography>
            )}
            {!product.isVariantParent && (
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.productPrice}
                />
            )}
            {product.unitOfMeasures && (
                <ProductUnitOfMeasureSelect
                    labelOverride=""
                    extendedStyles={styles.xProductDetailsPriceStyles?.productUnitOfMeasureSelectStyles}
                    data-test-selector="productSelector_uom"
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsPrice)),
    definition: {
        displayName: "Price",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
